import React, { useEffect, useState } from 'react';
import { Loader2, Search, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { transportApi } from '@/services/transportApi';

const END = '__end__';

/** Anyone adds a pickup / drop to a running (in-transit) trip; the driver accepts or rejects it. */
export const AddStopDialog = ({ trip, onClose, onDone }) => {
  const [kind, setKind] = useState('drop');
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState('');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [pin, setPin] = useState(null);
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [after, setAfter] = useState(END);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!trip) return;
    setKind('drop'); setLocId(''); setQ(''); setResults([]); setPin(null); setLabel(''); setNote(''); setAfter(END);
    transportApi.listLocations({ active: true }).then(setLocations).catch(() => setLocations([]));
  }, [trip]);

  const stops = trip?.stops || [];
  const firstOpen = (() => { let f = 0; stops.forEach((s, i) => { if ((s.status || 'PENDING') !== 'PENDING') f = i + 1; }); return f; })();
  const search = async () => {
    if (q.trim().length < 2) return;
    try { setResults(await transportApi.geocode(q.trim())); } catch { toast.error('Address search failed'); }
  };
  const pickSaved = (id) => { setLocId(id); setPin(null); const l = locations.find((x) => x.id === id); if (l && !label) setLabel(l.name); };
  const pickResult = (r) => { setPin({ lat: r.lat, lng: r.lng }); setLocId(''); setResults([]); if (!label) setLabel(r.name || (r.display_name || '').split(',')[0]); };

  const submit = async () => {
    if (!locId && !pin) { toast.error('Choose a saved place or search the address'); return; }
    setBusy(true);
    try {
      await transportApi.addLiveStop(trip.id, {
        kind, label, note, location_id: locId || null, lat: pin?.lat, lng: pin?.lng,
        position: after === END ? null : Number(after) + 1,
      });
      toast.success('Stop added — the driver has been notified to accept it');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add stop'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="add-stop-dialog">
        <DialogHeader>
          <DialogTitle>Add a stop to the running trip</DialogTitle>
          <DialogDescription>{trip?.vehicle_label} · {trip?.driver_name} · {trip?.route_summary}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2" data-testid="add-stop-kind">
            {['pickup', 'drop'].map((k) => (
              <Button key={k} type="button" size="sm" variant={kind === k ? 'default' : 'outline'} onClick={() => setKind(k)}
                className={kind === k ? (k === 'pickup' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700') : ''} data-testid={`add-stop-kind-${k}`}>
                {k === 'pickup' ? '▲ Pickup' : '▼ Drop'}
              </Button>
            ))}
          </div>
          <div className="space-y-1">
            <Label>Saved place</Label>
            <Select value={locId} onValueChange={pickSaved}>
              <SelectTrigger data-testid="add-stop-place-select"><SelectValue placeholder="Choose a saved place" /></SelectTrigger>
              <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>…or search an address</Label>
            <div className="flex gap-2">
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="Street, area, town" data-testid="add-stop-search-input" />
              <Button type="button" variant="outline" onClick={search} data-testid="add-stop-search-btn"><Search className="w-4 h-4" /></Button>
            </div>
            {results.length > 0 && (
              <div className="border rounded max-h-36 overflow-auto divide-y" data-testid="add-stop-search-results">
                {results.slice(0, 6).map((r, i) => (
                  <button key={i} type="button" onClick={() => pickResult(r)} className="w-full text-left text-xs px-2 py-1.5 hover:bg-gray-50" data-testid={`add-stop-result-${i}`}>{r.display_name || r.label}</button>
                ))}
              </div>
            )}
            {pin && <p className="text-[11px] text-emerald-700 flex items-center gap-1" data-testid="add-stop-pin"><MapPin className="w-3 h-3" />Pinned {pin.lat.toFixed(4)}, {pin.lng.toFixed(4)}</p>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label>Label</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Short name" data-testid="add-stop-label-input" /></div>
            <div className="space-y-1"><Label>Note for driver</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" data-testid="add-stop-note-input" /></div>
          </div>
          <div className="space-y-1">
            <Label>Place in route</Label>
            <Select value={after} onValueChange={setAfter}>
              <SelectTrigger data-testid="add-stop-position-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {stops.map((s, i) => (i >= Math.max(firstOpen - 1, 0) && i < stops.length - 1
                  ? <SelectItem key={s.stop_id || i} value={String(i)}>After stop {i + 1} · {s.label}</SelectItem> : null))}
                <SelectItem value={END}>At the end</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="add-stop-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="add-stop-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Add stop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddStopDialog;
